<script>
  import { createEventDispatcher } from 'svelte';
  import ChevronUp from "carbon-icons-svelte/lib/ChevronUp.svelte";
  import ChevronDown from "carbon-icons-svelte/lib/ChevronDown.svelte";
  import { calculateFleetDaysAndCosts } from "../Quotes/fleetScheduleCalculator.js";

  export let material = {};
  export let isEditMode = true;
  export let compact = false;
  export let showHaulageDuration = false;
  export let daysCount = null;

  const dispatch = createEventDispatcher();

  export const truckTypeOptions = [
    { id: "0", text: "8 wheeler - 16t", value: 16, defaultRate: 1350 },
    { id: "1", text: "10 wheeler - 19t", value: 19, defaultRate: 1400 },
    { id: "2", text: "Semi Trailer - 25t", value: 25, defaultRate: 1800 },
    { id: "3", text: "6 wheeler - 12t", value: 12, defaultRate: 1200 },
    { id: "4", text: "Truck & Dog - 19t", value: 19, defaultRate: 1650 }
  ];

  export const trailerTypeOptions = [
    { id: "0", text: "None", value: 0 },
    { id: "1", text: "6 wheeler - 15t", value: 15 },
    { id: "2", text: "8 wheeler - 20t", value: 19 },
    { id: "3", text: "Dog Trailer - 19t", value: 19 },
    { id: "4", text: "4 wheeler - 12t", value: 12 }
  ];

  // Defensive initialization of nested objects
  $: if (!material.fleet) {
    material.fleet = {
      truck_type: "10 wheeler - 19t",
      truck_capacity_tonne: 19.0,
      trailer_type: "8 wheeler - 20t",
      trailer_capacity_tonne: 19.0,
      total_capacity_tonne: 38.0,
      truck_day_rate: 1500.0
    };
  }

  $: if (!material.schedule) {
    const defaultTrucks = (() => {
      const pTrucks = parseInt(material.allocatedTrucks, 10);
      if (!isNaN(pTrucks) && pTrucks > 0) return pTrucks;
      if (typeof material.allocated_trucks === 'number' && material.allocated_trucks > 0) return material.allocated_trucks;
      return 1;
    })();
    material.schedule = {
      runs_per_day: 5.0,
      total_loads: 6,
      allocated_trucks: defaultTrucks,
      estimated_days: 0.9,
      days_single_truck: 0.9,
      full_days: 0,
      remainder_days: 0.9,
      total_tolls: 0.0
    };
  }

  $: if (!material.operating_hours) {
    material.operating_hours = {
      start_time: "07:00",
      finish_time: "17:00",
      load_mins: 10,
      tip_mins: 10
    };
  }

  // Reactive helpers for UI binding
  $: truckCap = Number(material.fleet?.truck_capacity_tonne != null ? material.fleet.truck_capacity_tonne : 19);
  $: trailerCap = Number(material.fleet?.trailer_capacity_tonne != null ? material.fleet.trailer_capacity_tonne : 19);
  $: totalCap = Number(material.fleet?.total_capacity_tonne || (truckCap + trailerCap) || 38);
  $: dayRateVal = Number(material.fleet?.truck_day_rate != null ? material.fleet.truck_day_rate : 1500);
  $: targetVolume = Number(material.amount != null ? material.amount : (material.total_tonnes || 0));
  $: allocatedTrucks = (() => {
    const sTrucks = parseInt(material.schedule?.allocated_trucks, 10);
    if (!isNaN(sTrucks) && sTrucks > 0) return sTrucks;
    const pTrucks = parseInt(material.allocatedTrucks, 10);
    if (!isNaN(pTrucks) && pTrucks > 0) return pTrucks;
    if (typeof material.allocated_trucks === 'number' && material.allocated_trucks > 0) {
      return material.allocated_trucks;
    }
    return 1;
  })();
  $: runsPerDay = Number(material.schedule?.runs_per_day != null ? material.schedule.runs_per_day : 5.0);
  $: totalLoads = Number(material.schedule?.total_loads != null ? material.schedule.total_loads : (totalCap > 0 && targetVolume > 0 ? Math.ceil(targetVolume / totalCap) : 0));
  $: estimatedDays = Number(material.schedule?.estimated_days != null ? material.schedule.estimated_days : 0);
  $: daysSingleTruck = Number(material.schedule?.days_single_truck != null ? material.schedule.days_single_truck : 0);
  $: effectiveDaysCount = daysCount != null
    ? Number(daysCount)
    : (material.days_count != null
        ? Number(material.days_count)
        : (material.schedule?.days_count != null
            ? Number(material.schedule.days_count)
            : (estimatedDays > 0 ? Math.max(1, Math.ceil(estimatedDays)) : 1)));

  $: startTimeVal = material.operating_hours?.start_time || "07:00";
  $: finishTimeVal = material.operating_hours?.finish_time || "17:00";
  $: loadMinsVal = Number(material.operating_hours?.load_mins ?? 10);
  $: tipMinsVal = Number(material.operating_hours?.tip_mins ?? 10);

  $: selectedTruckTypeId = getTruckTypeId(material.fleet);
  $: selectedTrailerTypeId = getTrailerTypeId(material.fleet);

  function getTruckTypeId(fleet) {
    if (!fleet) return "1";
    if (fleet.truck_type) {
      const match = truckTypeOptions.find(o => o.text === fleet.truck_type || o.id === String(fleet.truck_type));
      if (match) return match.id;
    }
    const cap = Number(fleet.truck_capacity_tonne ?? 19);
    const match = truckTypeOptions.find(o => o.value === cap);
    return match ? match.id : "1";
  }

  function getTrailerTypeId(fleet) {
    if (!fleet) return "2";
    if (fleet.trailer_type) {
      const match = trailerTypeOptions.find(o => o.text === fleet.trailer_type || o.id === String(fleet.trailer_type));
      if (match) return match.id;
    }
    const cap = Number(fleet.trailer_capacity_tonne ?? 19);
    const match = trailerTypeOptions.find(o => o.value === cap);
    return match ? match.id : "2";
  }

  function formatDays(days) {
    if (days === null || days === undefined || isNaN(days) || days <= 0) return "0 days";
    const num = Number(days);
    const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1);
    return `${formatted} ${num === 1 ? "day" : "days"}`;
  }

  function syncAndDispatch() {
    const tCap = Number(material.fleet?.truck_capacity_tonne != null ? material.fleet.truck_capacity_tonne : 19);
    const trCap = Number(material.fleet?.trailer_capacity_tonne != null ? material.fleet.trailer_capacity_tonne : 19);
    const tTotal = Math.max(1, tCap + trCap);
    const vol = Math.max(0, Number(material.amount != null ? material.amount : 0));
    const trucks = allocatedTrucks;
    const dRate = Number(material.fleet?.truck_day_rate != null ? material.fleet.truck_day_rate : 1500);

    const sTime = material.operating_hours?.start_time || "07:00";
    const fTime = material.operating_hours?.finish_time || "17:00";
    const lMins = Number(material.operating_hours?.load_mins != null ? material.operating_hours.load_mins : 10);
    const tMins = Number(material.operating_hours?.tip_mins != null ? material.operating_hours.tip_mins : 10);
    const trvMins = Number(material.travel_mins || material.travelMins || 30);
    const rts = material.routes || material.load?.routes || null;

    let runs = Number(material.schedule?.runs_per_day || 5.0);
    let loads = vol > 0 ? Math.ceil(vol / tTotal) : 0;
    let days1 = 0;
    let estD = 0;
    let fullD = 0;
    let remD = 0;

    try {
      const sim = calculateFleetDaysAndCosts({
        allocatedTrucks: trucks,
        totalTonnes: vol,
        truckCapacity: tTotal,
        dayRate: dRate,
        startTime: sTime,
        finishTime: fTime,
        loadTime: lMins,
        tipTime: tMins,
        routes: rts,
        mapDurationMinutes: trvMins,
        isEstimate: !rts || rts.length === 0,
        singleTruckDays: null,
        dailyRuns1Truck: null,
      });
      if (sim) {
        runs = Number((sim.dailyRuns1Truck || runs).toFixed(1));
        loads = sim.resolvedTotalLoads || loads;
        days1 = Number((sim.totalDays1Truck || (runs > 0 ? loads / runs : 0)).toFixed(1));
        estD = Number((sim.calculatedDays || (runs > 0 && trucks > 0 ? loads / (runs * trucks) : 0)).toFixed(1));
        fullD = sim.fullDays || 0;
        remD = sim.remainderDays || 0;
      }
    } catch (_) {
      if (runs > 0 && loads > 0) {
        days1 = Number((loads / runs).toFixed(1));
        estD = Number((loads / (trucks * runs)).toFixed(1));
      }
    }

    if (loads > 0 && (!estD || isNaN(estD))) {
      estD = Number((loads / (Math.max(0.1, runs) * trucks)).toFixed(1));
      days1 = Number((loads / Math.max(0.1, runs)).toFixed(1));
    }

    material.fleet = {
      ...material.fleet,
      truck_capacity_tonne: tCap,
      trailer_capacity_tonne: trCap,
      total_capacity_tonne: tTotal,
      truck_day_rate: dRate
    };

    material.operating_hours = {
      ...material.operating_hours,
      start_time: sTime,
      finish_time: fTime,
      load_mins: lMins,
      tip_mins: tMins
    };

    material.schedule = {
      ...material.schedule,
      allocated_trucks: trucks,
      total_loads: loads,
      runs_per_day: runs,
      days_single_truck: days1,
      estimated_days: estD,
      full_days: fullD,
      remainder_days: remD,
      days_count: effectiveDaysCount,
      total_tolls: Number(material.schedule?.total_tolls ?? 0.0)
    };

    material.days_count = effectiveDaysCount;
    material.amount = vol;

    // Clean up any legacy flat operational fields to ensure contract integrity
    delete material.start_time;
    delete material.finish_time;
    delete material.load_mins;
    delete material.tip_mins;
    delete material.runs_per_day;
    delete material.total_loads;
    delete material.estimated_days;
    delete material.days_single_truck;
    delete material.full_days;
    delete material.remainder_days;
    delete material.total_tolls;
    delete material.truck_day_rate;
    delete material.day_rate;
    delete material.total_tonnes;

    material = material;
    dispatch('change', { material });
  }

  let lastProcessedKey = null;
  $: {
    const key = material ? `${material.id || material._id || material.name || 'curr'}_${material.amount || 0}` : null;
    if (key && key !== lastProcessedKey) {
      lastProcessedKey = key;
      if (!material.schedule?.runs_per_day || !material.schedule?.estimated_days) {
        syncAndDispatch();
      }
    }
  }

  function handleTonnesInput(e) {
    const val = parseFloat(e.target.value);
    material.amount = isNaN(val) ? 0 : Math.max(0, val);
    syncAndDispatch();
  }

  function handleTruckTypeChange(e) {
    const opt = truckTypeOptions.find(o => o.id === e.target.value);
    if (!opt) return;
    if (!material.fleet) material.fleet = {};
    material.fleet.truck_type = opt.text;
    material.fleet.truck_capacity_tonne = opt.value;
    if (opt.defaultRate && (!material.fleet.truck_day_rate || material.fleet.truck_day_rate === 1400 || material.fleet.truck_day_rate === 1500)) {
      material.fleet.truck_day_rate = opt.defaultRate;
    }
    syncAndDispatch();
  }

  function handleTruckCapInput(e) {
    const val = parseFloat(e.target.value);
    if (!material.fleet) material.fleet = {};
    material.fleet.truck_capacity_tonne = isNaN(val) ? 0 : Math.max(0, val);
    const match = truckTypeOptions.find(o => o.value === material.fleet.truck_capacity_tonne);
    if (match) {
      material.fleet.truck_type = match.text;
    }
    syncAndDispatch();
  }

  function handleTrailerTypeChange(e) {
    const opt = trailerTypeOptions.find(o => o.id === e.target.value);
    if (!opt) return;
    if (!material.fleet) material.fleet = {};
    material.fleet.trailer_type = opt.text;
    material.fleet.trailer_capacity_tonne = opt.value;
    syncAndDispatch();
  }

  function handleTrailerCapInput(e) {
    const val = parseFloat(e.target.value);
    if (!material.fleet) material.fleet = {};
    material.fleet.trailer_capacity_tonne = isNaN(val) ? 0 : Math.max(0, val);
    const match = trailerTypeOptions.find(o => o.value === material.fleet.trailer_capacity_tonne);
    if (match) {
      material.fleet.trailer_type = match.text;
    }
    syncAndDispatch();
  }

  function handleDayRateInput(e) {
    const val = parseFloat(e.target.value);
    const rate = isNaN(val) ? 0 : Math.max(0, val);
    if (!material.fleet) material.fleet = {};
    material.fleet.truck_day_rate = rate;
    syncAndDispatch();
  }

  function handleTrucksInput(e) {
    const val = parseInt(e.target.value, 10);
    if (!material.schedule) material.schedule = {};
    material.schedule.allocated_trucks = isNaN(val) ? 1 : Math.max(1, val);
    syncAndDispatch();
  }

  function incrementTrucks() {
    if (!isEditMode) return;
    if (!material.schedule) material.schedule = {};
    material.schedule.allocated_trucks = Math.max(1, Number(material.schedule.allocated_trucks || 1) + 1);
    syncAndDispatch();
  }

  function decrementTrucks() {
    if (!isEditMode) return;
    if (!material.schedule) material.schedule = {};
    const current = Number(material.schedule.allocated_trucks || 1);
    if (current > 1) {
      material.schedule.allocated_trucks = current - 1;
      syncAndDispatch();
    }
  }

  function handleDaysStep(delta) {
    if (!isEditMode) return;
    const current = effectiveDaysCount;
    const next = Math.max(1, Math.min(60, current + delta));
    if (daysCount != null) {
      daysCount = next;
    }
    if (!material.schedule) material.schedule = {};
    material.schedule.days_count = next;
    material.days_count = next;
    dispatch('days-count-change', { delta, daysCount: next });
    syncAndDispatch();
  }

  function handleDaysInputChange(e) {
    if (!isEditMode) return;
    const val = parseInt(e.target.value, 10);
    const next = Math.max(1, Math.min(60, isNaN(val) ? 1 : val));
    if (daysCount != null) {
      daysCount = next;
    }
    if (!material.schedule) material.schedule = {};
    material.schedule.days_count = next;
    material.days_count = next;
    dispatch('days-count-change', { daysCount: next });
    syncAndDispatch();
  }

  function handleStartTimeInput(e) {
    const val = e.target.value || "07:00";
    if (!material.operating_hours) material.operating_hours = {};
    material.operating_hours.start_time = val;
    syncAndDispatch();
  }

  function handleFinishTimeInput(e) {
    const val = e.target.value || "17:00";
    if (!material.operating_hours) material.operating_hours = {};
    material.operating_hours.finish_time = val;
    syncAndDispatch();
  }

  function handleLoadMinsInput(e) {
    const val = parseFloat(e.target.value);
    if (!material.operating_hours) material.operating_hours = {};
    material.operating_hours.load_mins = isNaN(val) ? 10 : Math.max(0, val);
    syncAndDispatch();
  }

  function handleTipMinsInput(e) {
    const val = parseFloat(e.target.value);
    if (!material.operating_hours) material.operating_hours = {};
    material.operating_hours.tip_mins = isNaN(val) ? 10 : Math.max(0, val);
    syncAndDispatch();
  }
</script>

<div class="turnaround-card {compact ? 'is-compact' : ''}">
  <!-- Section 1: Operating Hours & Durations -->
  <div class="calc-section-title">Operating Hours &amp; Durations</div>
  <div class="calc-grid-5 mb-12">
    <div class="form-group">
      <label class="field-label" for="mat-total-tonnes-{material.id || 'curr'}">Total Tonnes</label>
      <input
        id="mat-total-tonnes-{material.id || 'curr'}"
        type="number"
        class="form-input"
        value={targetVolume}
        on:input={handleTonnesInput}
        min="0"
        step="50"
        placeholder="0"
        disabled={!isEditMode}
      />
    </div>
    <div class="form-group">
      <label class="field-label" for="mat-start-time-{material.id || 'curr'}">Start (24h)</label>
      <input
        id="mat-start-time-{material.id || 'curr'}"
        type="text"
        class="form-input"
        value={startTimeVal}
        on:input={handleStartTimeInput}
        placeholder="07:00"
        disabled={!isEditMode}
      />
    </div>
    <div class="form-group">
      <label class="field-label" for="mat-finish-time-{material.id || 'curr'}">Finish (24h)</label>
      <input
        id="mat-finish-time-{material.id || 'curr'}"
        type="text"
        class="form-input"
        value={finishTimeVal}
        on:input={handleFinishTimeInput}
        placeholder="17:00"
        disabled={!isEditMode}
      />
    </div>
    <div class="form-group">
      <label class="field-label" for="mat-load-time-{material.id || 'curr'}">Load (mins)</label>
      <input
        id="mat-load-time-{material.id || 'curr'}"
        type="number"
        class="form-input"
        value={loadMinsVal}
        on:input={handleLoadMinsInput}
        min="0"
        step="5"
        placeholder="10"
        disabled={!isEditMode}
      />
    </div>
    <div class="form-group">
      <label class="field-label" for="mat-tip-time-{material.id || 'curr'}">Tip (mins)</label>
      <input
        id="mat-tip-time-{material.id || 'curr'}"
        type="number"
        class="form-input"
        value={tipMinsVal}
        on:input={handleTipMinsInput}
        min="0"
        step="5"
        placeholder="10"
        disabled={!isEditMode}
      />
    </div>
  </div>

  <!-- Section 2: Fleet Specifications & Rates -->
  <div class="calc-section-title">Fleet Specifications &amp; Rates</div>
  <div class="calc-fleet-grid mb-12">
    <div class="form-group">
      <label class="field-label" for="mat-truck-type-{material.id || 'curr'}">Truck Type</label>
      <div class="select-wrapper">
        <select
          id="mat-truck-type-{material.id || 'curr'}"
          class="form-select"
          value={selectedTruckTypeId}
          on:change={handleTruckTypeChange}
          disabled={!isEditMode}
        >
          {#each truckTypeOptions as opt}
            <option value={opt.id}>{opt.text}</option>
          {/each}
        </select>
        <span class="select-chevron-icon"><ChevronDown size={14} /></span>
      </div>
    </div>

    <div class="form-group">
      <label class="field-label" for="mat-truck-cap-{material.id || 'curr'}">Truck (t)</label>
      <input
        id="mat-truck-cap-{material.id || 'curr'}"
        type="number"
        class="form-input fleet-input"
        value={truckCap}
        on:input={handleTruckCapInput}
        min="0"
        step="1"
        disabled={!isEditMode}
      />
    </div>

    <div class="form-group">
      <label class="field-label" for="mat-trailer-type-{material.id || 'curr'}">Trailer</label>
      <div class="select-wrapper">
        <select
          id="mat-trailer-type-{material.id || 'curr'}"
          class="form-select"
          value={selectedTrailerTypeId}
          on:change={handleTrailerTypeChange}
          disabled={!isEditMode}
        >
          {#each trailerTypeOptions as opt}
            <option value={opt.id}>{opt.text}</option>
          {/each}
        </select>
        <span class="select-chevron-icon"><ChevronDown size={14} /></span>
      </div>
    </div>

    <div class="form-group">
      <label class="field-label" for="mat-trailer-cap-{material.id || 'curr'}">Trailer (t)</label>
      <input
        id="mat-trailer-cap-{material.id || 'curr'}"
        type="number"
        class="form-input fleet-input"
        value={trailerCap}
        on:input={handleTrailerCapInput}
        min="0"
        step="1"
        disabled={!isEditMode}
      />
    </div>

    <div class="form-group">
      <label class="field-label" for="mat-day-rate-{material.id || 'curr'}">Day Rate ($)</label>
      <input
        id="mat-day-rate-{material.id || 'curr'}"
        type="number"
        class="form-input fleet-input"
        value={dayRateVal}
        on:input={handleDayRateInput}
        min="0"
        step="50"
        disabled={!isEditMode}
      />
    </div>
  </div>

  <!-- Section 3: Turnaround Schedule & Fleet Allocation -->
  <div class="calc-section-title">Turnaround Schedule &amp; Fleet Allocation</div>
  <div class="highlight-schedule-section">
    <div class="turnaround-schedule-grid {(showHaulageDuration || daysCount != null) ? 'has-duration' : ''}">
      <div class="schedule-metric-item">
        <span class="spec-label">Payload Capacity</span>
        <span class="spec-value">{totalCap} tonnes</span>
      </div>
      <div class="schedule-metric-item">
        <span class="spec-label">Daily Runs</span>
        <span class="spec-value">{runsPerDay > 0 ? `${runsPerDay} runs/pd` : '—'}</span>
      </div>
      <div class="schedule-metric-item">
        <span class="spec-label">Total Loads</span>
        <span class="spec-value">{totalLoads > 0 ? `${totalLoads.toLocaleString()} loads` : '—'}</span>
      </div>
      <div class="schedule-metric-item days-spec-item">
        <span class="spec-label">Days</span>
        <div class="days-display-group">
          <span class="spec-value days-val-highlight">{estimatedDays > 0 ? formatDays(estimatedDays) : '0 days'}</span>
          {#if daysSingleTruck > 0 && (allocatedTrucks > 1 || daysSingleTruck !== estimatedDays)}
            <span class="single-truck-note">({formatDays(daysSingleTruck)} w/ 1 truck)</span>
          {/if}
        </div>
      </div>
      <div class="schedule-metric-item trucks-spec-item">
        <span class="spec-label">Allocated Trucks</span>
        <div class="trucks-stepper-control">
          <input
            id="mat-allocated-trucks-{material.id || 'curr'}"
            type="number"
            class="trucks-stepper-input"
            min="1"
            max="50"
            step="1"
            value={allocatedTrucks}
            on:input={handleTrucksInput}
            disabled={!isEditMode}
          />
          {#if isEditMode}
            <div class="stepper-arrow-buttons">
              <button
                type="button"
                class="truck-step-btn step-up"
                on:click={incrementTrucks}
                title="Increase truck count"
                aria-label="Increase truck count"
              >
                <ChevronUp size={11} />
              </button>
              <button
                type="button"
                class="truck-step-btn step-down"
                on:click={decrementTrucks}
                disabled={allocatedTrucks <= 1}
                title="Decrease truck count"
                aria-label="Decrease truck count"
              >
                <ChevronDown size={11} />
              </button>
            </div>
          {/if}
        </div>
      </div>
      {#if showHaulageDuration || daysCount != null}
        <div class="schedule-metric-item duration-spec-item">
          <span class="spec-label">Haulage Duration</span>
          <div class="duration-stepper-control">
            <button 
              type="button" 
              class="duration-step-btn minus" 
              disabled={!isEditMode || effectiveDaysCount <= 1}
              on:click={() => handleDaysStep(-1)}
              title="Decrease duration by 1 day"
              aria-label="Decrease duration by 1 day"
            >
              &minus;
            </button>
            <span class="duration-val-badge">
              <strong>{effectiveDaysCount}</strong> Day{effectiveDaysCount !== 1 ? 's' : ''}
            </span>
            <button 
              type="button" 
              class="duration-step-btn plus" 
              disabled={!isEditMode || effectiveDaysCount >= 60}
              on:click={() => handleDaysStep(1)}
              title="Add another haulage day"
              aria-label="Add another haulage day"
            >
              +
            </button>
          </div>
          <span class="single-truck-note">Based on est. haulage rate</span>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .turnaround-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1.15rem;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  }

  .turnaround-card.is-compact {
    padding: 0.85rem 1rem;
  }

  /* Section Titles matching Quotes / Attachment 2 */
  .calc-section-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--tt-slate-500, #64748b);
    margin: 0.5rem 0 0.4rem 0;
  }

  .calc-section-title:first-child {
    margin-top: 0;
  }

  .mb-12 {
    margin-bottom: 0.75rem;
  }

  /* Form Elements */
  .form-group {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .field-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--tt-slate-600, #475569);
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .form-input {
    width: 100%;
    height: 38px;
    padding: 0.45rem 0.65rem;
    font-size: 0.85rem;
    font-weight: 500;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .form-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .form-input:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
  }

  /* 5-Column Grid for Operating Hours & Durations */
  .calc-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.5rem;
    align-items: end;
  }

  /* 5-Column Grid for Fleet Specifications & Rates */
  .calc-fleet-grid {
    display: grid;
    grid-template-columns: 1.7fr 0.9fr 1.7fr 1.2fr 1.5fr;
    gap: 0.5rem;
    align-items: end;
  }

  .fleet-input {
    height: 38px;
  }

  .select-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .form-select {
    width: 100%;
    height: 38px;
    padding: 0 1.8rem 0 0.75rem;
    font-size: 0.85rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    box-sizing: border-box;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .form-select:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .form-select:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
  }

  .select-chevron-icon {
    position: absolute;
    right: 0.65rem;
    color: var(--tt-slate-500, #64748b);
    pointer-events: none;
    display: flex;
    align-items: center;
  }

  /* Turnaround Schedule & Fleet Allocation Box (Attachment 2 style) */
  .highlight-schedule-section {
    background: linear-gradient(135deg, rgba(239, 246, 255, 0.85), rgba(240, 253, 244, 0.85)) !important;
    border: 1px solid #bfdbfe !important;
    border-radius: 6px;
    padding: 0.65rem 0.85rem;
    box-sizing: border-box;
  }

  .turnaround-schedule-grid {
    display: grid;
    grid-template-columns: 1.1fr 1fr 1fr 1.3fr 1.1fr;
    gap: 0.65rem;
    align-items: center;
  }

  .schedule-metric-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .spec-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
  }

  .spec-value {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .days-display-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .days-val-highlight {
    color: #1d4ed8 !important;
    font-weight: 800 !important;
    font-size: 1.05rem;
    line-height: 1.2;
  }

  .single-truck-note {
    font-size: 9.5px;
    font-weight: 500;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.1;
    white-space: nowrap;
  }

  /* Stepper Control matching Attachment 2 */
  .trucks-stepper-control {
    display: inline-flex;
    align-items: center;
    position: relative;
    width: 68px;
    height: 28px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 5px;
    transition: all 0.15s ease;
    overflow: hidden;
    margin-top: 1px;
    box-sizing: border-box;
  }

  .trucks-stepper-control:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .trucks-stepper-input {
    width: 44px;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
  }

  .trucks-stepper-input::-webkit-inner-spin-button,
  .trucks-stepper-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .stepper-arrow-buttons {
    display: flex;
    flex-direction: column;
    width: 22px;
    height: 100%;
    border-left: 1px solid var(--tt-slate-200, #e2e8f0);
    position: static;
  }

  .truck-step-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border: none;
    cursor: pointer;
    color: var(--tt-slate-600, #475569);
    transition: background 0.1s ease;
    padding: 0;
    width: 100%;
    height: 50%;
  }

  .truck-step-btn:first-child {
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .truck-step-btn:hover:not(:disabled) {
    background: #e2e8f0;
    color: #1d4ed8;
  }

  .truck-step-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  /* Haulage Duration Stepper in Highlight Schedule Section */
  .turnaround-schedule-grid.has-duration {
    grid-template-columns: 1.05fr 0.9fr 0.95fr 1.15fr 0.95fr 1.4fr;
    gap: 0.55rem;
  }

  .duration-spec-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .duration-stepper-control {
    display: inline-flex;
    align-items: center;
    height: 28px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 5px;
    overflow: hidden;
    margin-top: 1px;
    box-sizing: border-box;
    width: fit-content;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: all 0.15s ease;
  }

  .duration-stepper-control:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .duration-step-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 100%;
    border: none;
    background: #f8fafc;
    color: var(--tt-slate-700, #334155);
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    line-height: 1;
    transition: all 0.15s ease;
    padding: 0;
    user-select: none;
  }

  .duration-step-btn:hover:not(:disabled) {
    background-color: var(--tt-blue-50, #eff6ff);
    color: var(--tt-blue-600, #2563eb);
  }

  .duration-step-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .duration-val-badge {
    padding: 0 7px;
    font-size: 0.8rem;
    color: var(--tt-slate-800, #1e293b);
    border-left: 1px solid var(--tt-slate-200, #e2e8f0);
    border-right: 1px solid var(--tt-slate-200, #e2e8f0);
    white-space: nowrap;
    line-height: 26px;
    background: #ffffff;
    user-select: none;
  }

  .duration-val-badge strong {
    font-weight: 800;
    color: var(--tt-slate-900, #0f172a);
  }

  @media (max-width: 768px) {
    .calc-grid-5 {
      grid-template-columns: repeat(2, 1fr);
    }
    .calc-fleet-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .turnaround-schedule-grid,
    .turnaround-schedule-grid.has-duration {
      grid-template-columns: repeat(2, 1fr);
      row-gap: 0.75rem;
    }
  }
</style>
